import React, { useState, useEffect } from "react";
import {
  Search,
  Eye,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronRight,
  FileText,
  PlusCircle,
  Globe,
} from "lucide-react";
import axios from "axios";
import toast from "react-hot-toast";
import AddOrEditBlogModal from "../DashBoardBlog/AddBlogModal";
import BlogDetailsModal from "../DashBoardBlog/BlogDetailsModal";
import DeleteAlertModal from "../../common/DeleteAlertModal";

const AdminBlogList = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeLanguage, setActiveLanguage] = useState("en");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL;

  // Helper function to get text in current language
  const getText = (field, lang = activeLanguage) => {
    if (!field) return "";
    if (typeof field === "object" && field !== null) {
      return field[lang] || field.en || field.toString() || "";
    }
    return field || "";
  };

  // Helper function to check if field is multilingual
  const isMultilingual = (field) => {
    return field && typeof field === "object" && !Array.isArray(field);
  };

  // Fetch all blogs
  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/blog/get-all-blog`);
      const blogsArray = response?.data?.data || response?.data || []; 
      setBlogs(blogsArray);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load blogs!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  // Add New Blog
  const handleAddNew = () => {
    setSelectedBlog(null);
    setIsModalOpen(true);
  };

  // Edit Blog
  const handleEdit = (blog) => {
    setSelectedBlog(blog);
    setIsModalOpen(true);
  };

  // View Blog
  const handleViewBlog = (blog) => {
    setSelectedBlog(blog);
    setIsDetailsOpen(true);
  };

  // Delete Blog Confirmation
  const confirmDelete = (blog) => {
    setDeleteTarget(blog);
    setIsDeleteOpen(true);
  };

  // Delete Blog API
  const handleDelete = async () => {
    if (!deleteTarget?._id) return;
    try {
      setDeleteLoading(true);
      await axios.delete(`${API_URL}/blog/delete-blog/${deleteTarget._id}`);
      toast.success("Blog deleted successfully!");
      setIsDeleteOpen(false);
      fetchBlogs();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete blog!");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Filtering & Pagination
  const filteredBlogs = blogs.filter((blog) => {
    const title = getText(blog.title, "en").toLowerCase();
    const author = (blog.author || "").toLowerCase();
    const search = searchTerm.toLowerCase();
    
    return title.includes(search) || author.includes(search);
  });

  const totalPages = Math.ceil(filteredBlogs.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const currentBlogs = filteredBlogs.slice(startIndex, endIndex);

  // Language tabs component
  const LanguageTabs = () => (
    <div className="flex flex-wrap gap-2 mb-4">
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <Globe className="w-4 h-4" />
        <span className="font-medium">View Language:</span>
      </div>
      {["en", "hi", "es", "zh"].map((lang) => (
        <button
          key={lang}
          type="button"
          onClick={() => setActiveLanguage(lang)}
          className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
            activeLanguage === lang
              ? "bg-blue-500 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          {lang === "en" ? "English" : 
           lang === "hi" ? "हिंदी" : 
           lang === "es" ? "Español" : 
           "中文"}
        </button>
      ))}
    </div>
  );

  const SkeletonRow = () => (
    <tr className="border-b border-gray-100">
      {[...Array(6)].map((_, i) => (
        <td key={i} className="px-4 py-4">
          <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
        </td>
      ))}
    </tr>
  );

  return (
    <div className="min-h-screen">
      <div className="w-full mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-xl md:text-3xl font-philosopher text-primaryTextColor flex items-center gap-2">
            <FileText className="w-7 h-7 text-primaryColor" />
            Blog Management
          </h1>
        </div>

        {/* Language Selection */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <LanguageTabs />
          <p className="text-sm text-gray-500 mt-2">
            Currently viewing in:{" "}
            <span className="font-semibold">
              {activeLanguage === "en" ? "English" : 
               activeLanguage === "hi" ? "Hindi" : 
               activeLanguage === "es" ? "Spanish" : 
               "Chinese"}
            </span>
          </p>
        </div>

        {/* Search + Add */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="w-full md:w-auto">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search blogs by title or author..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="border border-gray-300 rounded-lg pl-4 pr-10 py-2 w-full md:w-96 focus:outline-none focus:ring-1 focus:ring-primaryColor"
                />
                <Search className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
              </div>
            </div>

            <button
              onClick={handleAddNew}
              className="bg-white border-2 border-blue-500 text-blue-500 px-6 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-50 transition whitespace-nowrap"
            >
              <PlusCircle className="w-5 h-5" />
              Add New Blog
            </button>
          </div>
        </div>

        {/* Blog Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">#</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Image</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">
                    Title ({activeLanguage === "en" ? "English" : activeLanguage === "hi" ? "Hindi" : activeLanguage === "es" ? "Spanish" : "Chinese"})
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Author</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Language Support</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Created</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => <SkeletonRow key={i} />)
                ) : currentBlogs.length > 0 ? (
                  currentBlogs.map((blog, index) => {
                    const title = getText(blog.title);
                    const content = getText(blog.content);
                    const isMultiTitle = isMultilingual(blog.title);
                    const isMultiContent = isMultilingual(blog.content);
                    
                    return (
                      <tr
                        key={blog._id}
                        className="border-b border-gray-100 hover:bg-gray-50 transition"
                      >
                        <td className="px-4 py-4 text-gray-500 font-medium">
                          {(currentPage - 1) * entriesPerPage + index + 1}
                        </td>
                        <td className="px-4 py-4">
                          <img
                            src={blog.image}
                            alt={title}
                            className="w-14 h-14 object-cover rounded-lg border border-gray-200"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "https://via.placeholder.com/56x56?text=No+Image";
                            }}
                          />
                        </td>
                        <td className="px-4 py-4">
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">
                              {title || "No title"}
                            </p>
                            {isMultiTitle && (
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                                  Multilingual
                                </span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          {blog.author || "Admin"}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-wrap gap-1">
                            {isMultiTitle || isMultiContent ? (
                              <>
                                {isMultiTitle && blog.title?.en && (
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                    EN
                                  </span>
                                )}
                                {isMultiTitle && blog.title?.hi && (
                                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                    HI
                                  </span>
                                )}
                                {isMultiTitle && blog.title?.es && (
                                  <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                                    ES
                                  </span>
                                )}
                                {isMultiTitle && blog.title?.zh && (
                                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
                                    ZH
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                                English Only
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">
                          {new Date(blog.createdAt).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              className="p-2 hover:bg-blue-50 rounded-lg transition"
                              title="View"
                              onClick={() => handleViewBlog(blog)}
                            >
                              <Eye className="w-4 h-4 text-[#ea8d2a]" />
                            </button>
                            <button
                              className="p-2 hover:bg-green-50 rounded-lg transition"
                              title="Edit"
                              onClick={() => handleEdit(blog)}
                            >
                              <Pencil className="w-4 h-4 text-[#6278e8]" />
                            </button>
                            <button
                              className="p-2 hover:bg-red-50 rounded-lg transition"
                              title="Delete"
                              onClick={() => confirmDelete(blog)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-12 text-center text-gray-500"
                    >
                      <div className="flex flex-col items-center justify-center">
                        <FileText className="w-12 h-12 text-gray-300 mb-3" />
                        <p className="text-lg font-medium text-gray-600 mb-1">No blogs found</p>
                        <p className="text-sm text-gray-500">
                          {searchTerm ? "Try a different search term" : "Add your first blog to get started"}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && filteredBlogs.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="text-sm text-gray-600">
                  Showing {startIndex + 1} to{" "}
                  {Math.min(endIndex, filteredBlogs.length)} of{" "}
                  {filteredBlogs.length} blogs
                </div>
                
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Show:</span>
                  <select
                    value={entriesPerPage}
                    onChange={(e) => {
                      setEntriesPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                  </select>
                  <span className="text-sm text-gray-600">entries</span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  title="Previous page"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                <div className="flex items-center gap-1">
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={i}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 rounded-lg transition text-sm ${
                          currentPage === pageNum
                            ? "bg-blue-500 text-white"
                            : "border border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  {totalPages > 5 && currentPage < totalPages - 2 && (
                    <>
                      <span className="px-1 text-gray-400">...</span>
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        className={`px-3 py-1 rounded-lg transition text-sm border border-gray-300 hover:bg-gray-50`}
                      >
                        {totalPages}
                      </button>
                    </>
                  )}
                </div>

                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  title="Next page"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <AddOrEditBlogModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={fetchBlogs}
        blog={selectedBlog}
      />

      {/* Details Modal */}
      <BlogDetailsModal
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        blog={selectedBlog}
      />

      {/* Common Delete Modal */}
      <DeleteAlertModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        dltType="blog"
        dltName={getText(deleteTarget?.title, "en")}
        onConfirm={handleDelete}
        dltLoading={deleteLoading}
      />
    </div>
  );
};

export default AdminBlogList;





// import React, { useState, useEffect } from "react";
// import {
//   Search,
//   Eye,
//   Pencil,
//   Trash2,
//   ChevronLeft,
//   ChevronRight,
//   FileText,
//   PlusCircle,
// } from "lucide-react";
// import axios from "axios";
// import toast from "react-hot-toast";
// import AddOrEditBlogModal from "../DashBoardBlog/AddBlogModal";
// import BlogDetailsModal from "../DashBoardBlog/BlogDetailsModal";
// import DeleteAlertModal from "../../common/DeleteAlertModal";

// const AdminBlogList = () => {
//   const [blogs, setBlogs] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [entriesPerPage, setEntriesPerPage] = useState(10);
//   const [currentPage, setCurrentPage] = useState(1);

//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [selectedBlog, setSelectedBlog] = useState(null);
//   const [isDetailsOpen, setIsDetailsOpen] = useState(false);

//   const [isDeleteOpen, setIsDeleteOpen] = useState(false);
//   const [deleteLoading, setDeleteLoading] = useState(false);
//   const [deleteTarget, setDeleteTarget] = useState(null);

//   const API_URL = process.env.REACT_APP_API_URL;

//   // Fetch all blogs
// const fetchBlogs = async () => {
//   try {
//     setLoading(true);
//     const response = await axios.get(`${process.env.REACT_APP_API_URL}/blog/get-all-blog`);
//     const blogsArray = response?.data?.data || response?.data || []; 
//     setBlogs(blogsArray);
//   } catch (err) {
//     console.error(err);
//     toast.error("Failed to load blogs!");
//   } finally {
//     setLoading(false);
//   }
// };


//   useEffect(() => {
//     fetchBlogs();
//   }, []);

//   // Add New Blog
//   const handleAddNew = () => {
//     setSelectedBlog(null);
//     setIsModalOpen(true);
//   };

//   // Edit Blog
//   const handleEdit = (blog) => {
//     setSelectedBlog(blog);
//     setIsModalOpen(true);
//   };

//   // View Blog
//   const handleViewBlog = (blog) => {
//     setSelectedBlog(blog);
//     setIsDetailsOpen(true);
//   };

//   // Delete Blog Confirmation
//   const confirmDelete = (blog) => {
//     setDeleteTarget(blog);
//     setIsDeleteOpen(true);
//   };

//   // Delete Blog API
//   const handleDelete = async () => {
//     if (!deleteTarget?._id) return;
//     try {
//       setDeleteLoading(true);
//       await axios.delete(`${API_URL}/blog/delete-blog/${deleteTarget._id}`);
//       toast.success("Blog deleted successfully!");
//       setIsDeleteOpen(false);
//       fetchBlogs();
//     } catch (err) {
//       console.error(err);
//       toast.error("Failed to delete blog!");
//     } finally {
//       setDeleteLoading(false);
//     }
//   };

//   // Filtering & Pagination
//   const filteredBlogs = blogs.filter(
//     (b) =>
//       b.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       b.author?.toLowerCase().includes(searchTerm.toLowerCase())
//   );

//   const totalPages = Math.ceil(filteredBlogs.length / entriesPerPage);
//   const startIndex = (currentPage - 1) * entriesPerPage;
//   const endIndex = startIndex + entriesPerPage;
//   const currentBlogs = filteredBlogs.slice(startIndex, endIndex);

//   const SkeletonRow = () => (
//     <tr className="border-b border-gray-100">
//       {[...Array(6)].map((_, i) => (
//         <td key={i} className="px-4 py-4">
//           <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
//         </td>
//       ))}
//     </tr>
//   );

//   return (
//     <div className="min-h-screen">
//       <div className="w-full mx-auto">
//         {/* Header */}
//         <div className="flex justify-between items-center mb-6">
//           <h1 className="text-xl md:text-3xl font-philosopher text-primaryTextColor flex items-center gap-2">
//             <FileText className="w-7 h-7 text-primaryColor" />
//             Blog Management
//           </h1>
//         </div>

//         {/* Search + Add */}
//         <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
//           <div className="flex justify-between items-center gap-4">
//             <div className="relative">
//               <input
//                 type="text"
//                 placeholder="Search blogs..."
//                 value={searchTerm}
//                 onChange={(e) => {
//                   setSearchTerm(e.target.value);
//                   setCurrentPage(1);
//                 }}
//                 className="border border-gray-300 rounded-lg pl-4 pr-10 py-2 w-full md:w-96 focus:outline-none focus:ring-1 focus:ring-primaryColor"
//               />
//               <Search className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
//             </div>

//             <button
//               onClick={handleAddNew}
//               className="bg-white border-2 border-blue-500 text-blue-500 px-6 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-50 transition"
//             >
//               <PlusCircle className="w-5 h-5" />
//               Add New Blog
//             </button>
//           </div>
//         </div>

//         {/* Blog Table */}
//         <div className="bg-white rounded-lg shadow-sm overflow-hidden">
//           <div className="overflow-x-auto">
//             <table className="w-full">
//               <thead className="bg-gray-50 border-b border-gray-200">
//                 <tr>
//                   <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">#</th>
//                   <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Image</th>
//                   <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Title</th>
//                   <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Author</th>
//                   <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Created</th>
//                   <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {loading ? (
//                   [...Array(5)].map((_, i) => <SkeletonRow key={i} />)
//                 ) : currentBlogs.length > 0 ? (
//                   currentBlogs.map((blog, index) => (
//                     <tr
//                       key={blog._id}
//                       className="border-b border-gray-100 hover:bg-gray-50 transition"
//                     >
//                       <td className="px-4 py-4 text-gray-500 font-medium">
//                         {(currentPage - 1) * entriesPerPage + index + 1}
//                       </td>
//                       <td className="px-4 py-4">
//                         <img
//                           src={blog.image}
//                           alt={blog.title}
//                           className="w-14 h-14 object-cover rounded-lg border border-gray-200"
//                         />
//                       </td>
//                       <td className="px-4 py-4 text-sm font-medium text-gray-900">
//                         {blog.title}
//                       </td>
//                       <td className="px-4 py-4 text-sm text-gray-700">
//                         {blog.author || "Admin"}
//                       </td>
//                       <td className="px-4 py-4 text-sm text-gray-600">
//                         {new Date(blog.createdAt).toLocaleDateString()}
//                       </td>
//                       <td className="px-4 py-4">
//                         <div className="flex items-center gap-2">
//                           <button
//                             className="p-2 hover:bg-blue-50 rounded-lg transition"
//                             title="View"
//                             onClick={() => handleViewBlog(blog)}
//                           >
//                             <Eye className="w-4 h-4 text-[#ea8d2a]" />
//                           </button>
//                           <button
//                             className="p-2 hover:bg-green-50 rounded-lg transition"
//                             title="Edit"
//                             onClick={() => handleEdit(blog)}
//                           >
//                             <Pencil className="w-4 h-4 text-[#6278e8]" />
//                           </button>
//                           <button
//                             className="p-2 hover:bg-red-50 rounded-lg transition"
//                             title="Delete"
//                             onClick={() => confirmDelete(blog)}
//                           >
//                             <Trash2 className="w-4 h-4 text-red-500" />
//                           </button>
//                         </div>
//                       </td>
//                     </tr>
//                   ))
//                 ) : (
//                   <tr>
//                     <td
//                       colSpan={6}
//                       className="px-4 py-8 text-center text-gray-500"
//                     >
//                       No blogs found
//                     </td>
//                   </tr>
//                 )}
//               </tbody>
//             </table>
//           </div>

//           {/* Pagination */}
//           {!loading && filteredBlogs.length > 0 && (
//             <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
//               <div className="text-sm text-gray-600">
//                 Showing {startIndex + 1} to{" "}
//                 {Math.min(endIndex, filteredBlogs.length)} of{" "}
//                 {filteredBlogs.length} blogs
//               </div>

//               <div className="flex items-center gap-2">
//                 <button
//                   onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
//                   disabled={currentPage === 1}
//                   className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
//                 >
//                   <ChevronLeft className="w-5 h-5" />
//                 </button>

//                 {[...Array(totalPages)].map((_, i) => (
//                   <button
//                     key={i}
//                     onClick={() => setCurrentPage(i + 1)}
//                     className={`px-4 py-2 rounded-lg transition ${
//                       currentPage === i + 1
//                         ? "bg-blue-500 text-white"
//                         : "border border-gray-300 hover:bg-gray-50"
//                     }`}
//                   >
//                     {i + 1}
//                   </button>
//                 ))}

//                 <button
//                   onClick={() =>
//                     setCurrentPage((p) => Math.min(totalPages, p + 1))
//                   }
//                   disabled={currentPage === totalPages}
//                   className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
//                 >
//                   <ChevronRight className="w-5 h-5" />
//                 </button>
//               </div>
//             </div>
//           )}
//         </div>
//       </div>

//       {/* Add/Edit Modal */}
//       <AddOrEditBlogModal
//         isOpen={isModalOpen}
//         onClose={() => setIsModalOpen(false)}
//         onSuccess={fetchBlogs}
//         blog={selectedBlog}
//       />

//       {/* Details Modal */}
//       <BlogDetailsModal
//         isOpen={isDetailsOpen}
//         onClose={() => setIsDetailsOpen(false)}
//         blog={selectedBlog}
//       />

//       {/* Common Delete Modal */}
//       <DeleteAlertModal
//         isOpen={isDeleteOpen}
//         onClose={() => setIsDeleteOpen(false)}
//         dltType="blog"
//         dltName={deleteTarget?.title}
//         onConfirm={handleDelete}
//         dltLoading={deleteLoading}
//       />
//     </div>
//   );
// };

// export default AdminBlogList;
